import Service from "lib/models/Service";
import { formatDate, formatArray } from "lib/formatter";

import {
  NewsSource,
  MajorTagSource,
  TagSource,
  MajorTag,
  Tag,
  NewsSummarySource,
} from "./models";
import NewsFilter from "./commands/NewsFilter";
import NewsForm, { NewsFormSource } from "./commands/NewsForm";
import AddTagForm from "./commands/AddTagForm";

interface PaginationSource {
  count: number;
  next?: string;
  previous?: string;
  results: any[];
}

export interface NewsSearchResult {
  total: number;
  page: number;
  ids: number[];
  filter: NewsFilter;
}

export default class NewsService extends Service {
  public async findNews(
    filter: NewsFilter,
    page: number,
    size: number,
  ): Promise<{
    result: NewsSearchResult;
    sources: NewsSource[];
  }> {
    try {
      const res: PaginationSource = await this.get("/news", {
        preset: formatArray(filter.preset),
        startDate: formatDate(filter.startDate),
        endDate: formatDate(filter.endDate),
        source: formatArray(filter.source),
        tag: formatArray(filter.tag),
        ordering: this.ordering(filter.orderBy, filter.sort),
        page,
        size,
      });
      if (!res) {
        throw res;
      }
      return {
        result: {
          ids: res.results.map((news) => news.id),
          total: res.count,
          page,
          filter,
        },
        sources: res.results,
      };
    } catch (e) {
      throw e;
    }
  }

  public async getNews(newsId: number): Promise<{ form: NewsFormSource }> {
    try {
      const res = await this.get(`/news/${newsId}`);
      if (!res) {
        throw res;
      }
      return {
        form: {
          title: res.title || "",
          source: res.source || "",
          imageUrl: res.imageUrl || "",
          originalUrl: res.originalUrl || "",
          publishedDate: res.publishedDate || "",
          summary: res.summary || "",
          isChecked: res.isChecked || false,
          isDismissed: res.isDismissed || false,
          isDismissedByAi: res.isDismissedByAi || false,
          sourceImageUrl: res.sourceImageUrl || "",
          tags: res.tags || [],
          tagsByAi: res.tagsByAi || [],
          content: res.content || "",
        },
      };
    } catch (e) {
      throw e;
    }
  }

  public async getNewsPreview(url: string): Promise<NewsSource> {
    try {
      const res = await this.get(`/news/preview`, {
        url,
      });
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async addNews(form: NewsForm): Promise<boolean> {
    try {
      const res = await this.post("/news", form.data);
      if (!res) {
        throw res;
      }
      return !!res;
    } catch (e) {
      throw e;
    }
  }

  public async updateNews(newsId: number, form: NewsForm): Promise<boolean> {
    try {
      const res = await this.put(`/news/${newsId}`, form.data);
      if (!res) {
        throw res;
      }
      return !!res;
    } catch (e) {
      throw e;
    }
  }

  public async findSources(): Promise<string[]> {
    try {
      const res: string[] = await this.get("/news/sources");
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async findMajorTags(): Promise<MajorTag[]> {
    try {
      const res: MajorTagSource[] = await this.get("/news/major-tags");
      if (!res) {
        throw res;
      }
      return res.map((tag) => new MajorTag(tag.id, tag));
    } catch (e) {
      throw e;
    }
  }

  public async findTags(): Promise<Tag[]> {
    try {
      const res: TagSource[] = await this.get("/news/tags");
      if (!res) {
        throw res;
      }
      return res.map((tag) => new Tag(tag.id, tag));
    } catch (e) {
      throw e;
    }
  }

  public async findSummary(): Promise<NewsSummarySource> {
    try {
      const res = await this.get("/news/summary");
      if (!res) {
        throw res;
      }
      return res;
    } catch (e) {
      throw e;
    }
  }

  public async addTag(form: AddTagForm): Promise<boolean> {
    try {
      const res = await this.post("/news/tags", {
        majorId: form.majorId,
        name: form.name,
      });
      if (!res) {
        throw res;
      }
      return !!res;
    } catch (e) {
      throw e;
    }
  }

  public async dismissNews(newsId: number): Promise<boolean> {
    try {
      const res = await this.put(`/news/${newsId}/dismiss`);
      if (!res) {
        throw res;
      }
      return !!res;
    } catch (e) {
      throw e;
    }
  }

  public async restoreNews(newsId: number): Promise<boolean> {
    try {
      const res = await this.put(`/news/${newsId}/restore`);
      if (!res) {
        throw res;
      }
      return !!res;
    } catch (e) {
      throw e;
    }
  }
}
